import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress
} from "@mui/material";
import {
  CheckCircleOutline,
  HourglassEmpty,
  CancelOutlined,
  DoneAll,
  AssignmentTurnedIn
} from "@mui/icons-material";
import api from "../../../api/axiosInterceptor";
import { API_BASE_URL } from "../../../config";
import { SnackBarComponent } from "../../../Components/snackBar/snackBar";

export default function ClaimDashboard() {
  const [counts, setCounts] = useState({
    total: 0,
    ca_approved: 0,
    rejected: 0,
    settled: 0,
    in_progress: 0
  });
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info"
  });

  const showSnackbar = (msg, sev = "info") =>
    setSnackbar({ open: true, message: msg, severity: sev });
  const closeSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const fetchClaimCounts = async () => {
    setLoading(true);
    try {
      const res = await api.get(
        `/claim-mgmt/users-claims-count/`
      );
      setCounts(res.data);
    } catch (err) {
      console.error("Error fetching claim counts", err);
      showSnackbar("Failed to load dashboard data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClaimCounts();
  }, []);

  const cards = [
    {
      title: "Total Claims",
      value: counts.total,
      icon: <AssignmentTurnedIn sx={{ fontSize: 50, color: "white" }} />,
      bgColor: "linear-gradient(135deg, #6D83F2 0%, #A1B8FF 100%)"
    },
    {
      title: "CA Approved",
      value: counts.ca_approved,
      icon: <CheckCircleOutline sx={{ fontSize: 50, color: "white" }} />,
      bgColor: "linear-gradient(135deg, #34D399 0%, #10B981 100%)"
    },
    {
      title: "Rejected",
      value: counts.rejected,
      icon: <CancelOutlined sx={{ fontSize: 50, color: "white" }} />,
      bgColor: "linear-gradient(135deg, #F87171 0%, #EF4444 100%)"
    },
    {
      title: "Settled",
      value: counts.settled,
      icon: <DoneAll sx={{ fontSize: 50, color: "white" }} />,
      bgColor: "linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%)"
    },
    {
      title: "In Progress",
      value: counts.in_progress,
      icon: <HourglassEmpty sx={{ fontSize: 50, color: "white" }} />,
      bgColor: "linear-gradient(135deg, #60A5FA 0%, #2563EB 100%)"
    }
  ];

  return (
    <Box sx={{ p: 4 }}>
      <Typography
        variant="h4"
        sx={{
          mb: 4,
          fontWeight: "bold",
          textAlign: "center",
          background: "linear-gradient(90deg, #2563EB, #34D399, #F59E0B)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          textShadow: "1px 1px 2px rgba(0, 0, 0, 0.3)"
        }}
      >
        Claim Management Dashboard
      </Typography>

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{ height: "50vh" }}
        >
          <CircularProgress size={60} />
        </Box>
      ) : (
        <Box
          display="flex"
          flexWrap="wrap"
          justifyContent="center"
          gap={3}
        >
          {cards.map((card, idx) => (
            <Card
              key={idx}
              sx={{
                flex: "1 1 220px",
                maxWidth: "260px",
                background: card.bgColor,
                color: "white",
                borderRadius: 3,
                textAlign: "center",
                boxShadow: 5,
                transition: "transform 0.2s ease-in-out",
                "&:hover": { transform: "translateY(-5px)" }
              }}
            >
              <CardContent>
                <Box display="flex" justifyContent="center" mb={2}>
                  {card.icon}
                </Box>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {card.title}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                  {card.value}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <SnackBarComponent
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={closeSnackbar}
      />
    </Box>
  );
}
